/**
 * useSavedAddresses - Hook for managing saved withdrawal addresses
 * 
 * Provides CRUD operations for user's "address book" of external crypto wallets.
 * These addresses are stored locally (not in Bridge API) for quick withdrawal access.
 * 
 * @see 20251204120000_create_saved_withdrawal_addresses.sql
 */

import { supabaseBrowser } from '@/lib/supabase-browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';

// Types
export interface SavedAddress {
  id: string;
  label: string;
  address: string;
  chain: string;
  currency: string;
  verified: boolean;
  last_used_at: string | null;
  use_count: number;
  created_at: string;
}

export interface AddSavedAddressParams {
  label: string;
  address: string;
  chain: string;
  currency: string;
}

// Query key
const SAVED_ADDRESSES_KEY = ['user', 'saved-addresses'];

/**
 * Get all saved addresses for current user
 */
export function useSavedAddresses() {
  return useQuery({
    queryKey: SAVED_ADDRESSES_KEY,
    queryFn: async (): Promise<SavedAddress[]> => {
      const { data, error } = await supabaseBrowser
        .rpc('get_user_saved_addresses');

      if (error) {
        console.error('[useSavedAddresses] Error fetching:', error);
        throw new Error(error.message);
      }

      return data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Add a new saved address
 */
export function useAddSavedAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: AddSavedAddressParams) => {
      const { data, error } = await supabaseBrowser
        .rpc('add_user_saved_address', {
          p_label: params.label,
          p_address: params.address,
          p_chain: params.chain,
          p_currency: params.currency,
        });

      if (error) {
        console.error('[useAddSavedAddress] RPC error:', error);
        throw new Error(error.message);
      }

      const result = data as { ok: boolean; error?: string; data?: SavedAddress };

      if (!result.ok) {
        throw new Error(result.error || 'Failed to add address');
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SAVED_ADDRESSES_KEY });
      toast.success('Address saved successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save address');
    },
  });
}

/**
 * Update a saved address (label only)
 */
export function useUpdateSavedAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, label }: { id: string; label: string }) => {
      const { data, error } = await supabaseBrowser
        .rpc('update_user_saved_address', {
          p_id: id,
          p_label: label,
        });

      if (error) {
        console.error('[useUpdateSavedAddress] RPC error:', error);
        throw new Error(error.message);
      }

      const result = data as { ok: boolean; error?: string };

      if (!result.ok) {
        throw new Error(result.error || 'Failed to update address');
      }

      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SAVED_ADDRESSES_KEY });
      toast.success('Address updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update address');
    },
  });
}

/**
 * Delete a saved address
 */
export function useDeleteSavedAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabaseBrowser
        .rpc('delete_user_saved_address', {
          p_id: id,
        });

      if (error) {
        console.error('[useDeleteSavedAddress] RPC error:', error);
        throw new Error(error.message);
      }

      const result = data as { ok: boolean; error?: string };

      if (!result.ok) {
        throw new Error(result.error || 'Failed to delete address');
      }

      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SAVED_ADDRESSES_KEY });
      toast.success('Address removed');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove address');
    },
  });
}

/**
 * Mark address as used (for sorting)
 */
export function useMarkAddressUsed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabaseBrowser
        .rpc('mark_saved_address_used', {
          p_id: id,
        });

      if (error) {
        console.error('[useMarkAddressUsed] RPC error:', error);
        // Don't throw - this is non-critical
        return false;
      }

      const result = data as { ok: boolean };
      return result?.ok || false;
    },
    onSuccess: () => {
      // Silently invalidate to update order
      queryClient.invalidateQueries({ queryKey: SAVED_ADDRESSES_KEY });
    },
  });
}

// Supported chains for the UI
export const SUPPORTED_CHAINS = [
  { value: 'ethereum', label: 'Ethereum', icon: 'ethereum' },
  { value: 'base', label: 'Base', icon: 'base' },
  { value: 'solana', label: 'Solana', icon: 'solana' },
  { value: 'polygon', label: 'Polygon', icon: 'polygon' },
  { value: 'arbitrum', label: 'Arbitrum', icon: 'arbitrum' },
  { value: 'optimism', label: 'Optimism', icon: 'optimism' },
  { value: 'avalanche', label: 'Avalanche', icon: 'avalanche' },
  { value: 'tron', label: 'Tron', icon: 'tron' },
  { value: 'stellar', label: 'Stellar', icon: 'stellar' },
] as const;

export type SupportedChain = typeof SUPPORTED_CHAINS[number]['value'];

// Supported currencies/tokens for withdrawals
export const SUPPORTED_CURRENCIES = [
  { value: 'usdc', label: 'USDC', icon: 'usdc' },
  { value: 'usdt', label: 'USDT', icon: 'usdt' },
  { value: 'usdb', label: 'USDB', icon: 'usdb' },
  { value: 'eth', label: 'ETH', icon: 'eth' },
  { value: 'sol', label: 'SOL', icon: 'sol' },
  { value: 'matic', label: 'MATIC', icon: 'matic' },
] as const;

export type SupportedCurrency = typeof SUPPORTED_CURRENCIES[number]['value'];

// Chain-currency compatibility (which tokens are available on which chains)
export const CHAIN_CURRENCIES: Record<string, string[]> = {
  ethereum: ['usdc', 'usdt', 'eth', 'usdb'],
  base: ['usdc', 'usdb', 'eth'],
  solana: ['usdc', 'usdt', 'sol'],
  polygon: ['usdc', 'usdt', 'matic'],
  arbitrum: ['usdc', 'usdt', 'eth'],
  optimism: ['usdc', 'usdt', 'eth'],
  avalanche: ['usdc', 'usdt'],
  tron: ['usdt'],
  stellar: ['usdc'],
};

